import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CalendarPanelProps } from './index'

export function useCalendarPanel({
  viewDate,
  minDate,
  maxDate,
  onSetViewDate,
}: Pick<
  CalendarPanelProps,
  'viewDate' | 'minDate' | 'maxDate' | 'onSetViewDate'
>) {
  const { i18n, t } = useTranslation()
  const [isDropdownMonthYearOpen, setIsDropdownMonthYearOpen] = useState(false)

  const monthYear = new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    year: 'numeric',
  }).format(viewDate)
  const weekdays = t('calendar.weekdays', { returnObjects: true }) as string[]

  const viewYM = viewDate.getFullYear() * 12 + viewDate.getMonth()
  const isPrevDisabled =
    !!minDate && viewYM <= minDate.getFullYear() * 12 + minDate.getMonth()
  const isNextDisabled =
    !!maxDate && viewYM >= maxDate.getFullYear() * 12 + maxDate.getMonth()

  const currentYear = viewDate.getFullYear()

  const monthNames = Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(
      new Date(2000, i, 1),
    ),
  )

  const monthOptions = monthNames
    .map((label, i) => ({ value: String(i), label }))
    .filter(({ value }) => {
      const m = Number(value)
      if (
        minDate &&
        currentYear === minDate.getFullYear() &&
        m < minDate.getMonth()
      )
        return false
      if (
        maxDate &&
        currentYear === maxDate.getFullYear() &&
        m > maxDate.getMonth()
      )
        return false
      return true
    })

  const yearStart = minDate ? minDate.getFullYear() : currentYear - 100
  const yearEnd = maxDate ? maxDate.getFullYear() : currentYear + 10
  const yearOptions = Array.from(
    { length: yearEnd - yearStart + 1 },
    (_, i) => ({ value: String(yearEnd - i), label: String(yearEnd - i) }),
  )

  function handleYearSelect(v: string) {
    const year = Number(v)
    let month = viewDate.getMonth()
    if (minDate && year === minDate.getFullYear())
      month = Math.max(month, minDate.getMonth())
    if (maxDate && year === maxDate.getFullYear())
      month = Math.min(month, maxDate.getMonth())
    onSetViewDate?.(new Date(year, month, 1))
  }

  return {
    t,
    monthYear,
    weekdays,
    isPrevDisabled,
    isNextDisabled,
    currentYear,
    monthOptions,
    yearOptions,
    isDropdownMonthYearOpen,
    setIsDropdownMonthYearOpen,
    handleYearSelect,
  }
}
